import { Box, Grid, GridItem } from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import type React from 'react';
import { useMemo } from 'react';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import type { ReturnOrderFormData } from '@/components/Templates/Returns/ReturnOrderForm/types';
import { useClinicStore } from '@/store/clinicStore';
import type { FormMethods } from '@/types/form';

export interface CustomerInformationProps {
	formMethods: FormMethods<ReturnOrderFormData>;
}

const CustomerInformation: React.FC<CustomerInformationProps> = ({
	formMethods,
}) => {
	const { watch, errors, setValue, register } = formMethods;

	// Get clinics from store instead of fetching from API
	const clinicsFromStore = useClinicStore((state) => state.currentClinics);

	// Transform clinics from store to SelectItem format
	const clinics = useMemo(() => 
		clinicsFromStore.map((clinic) => ({
			value: clinic.accountid,
			label: clinic.name,
		})) as SelectItem[],
	[clinicsFromStore]);

	const stateZipRegister = register('ta_statezip');

	return (
		<Box mb={-6}>
		<FaceliftText
			fontSize={{ base: 'sm', md: 'md' }}
			fontWeight="semibold"
			mb={{ base: 4, md: 6 }}
			fontStyle="italic"
		>
			Patient Information
		</FaceliftText>

		<Grid
			templateColumns={{
				base: '1fr',
				sm: 'repeat(1, 1fr)',
				md: 'repeat(3, 1fr)',
			}}
			gap={{ base: 4, sm: 5, md: 6 }}
			alignItems="start"
		>
			{/* Customer Name (Clinic) */}
			<GridItem>
				<FaceliftSelect<SelectItem, false>
					topLabel="Customer Name"
					required
					placeholder="Type Customer Name"
					value={
						clinics.find(
							(item) => item.value === (watch('clinic') as SelectItem)?.value,
						) || null
					}
					onChange={(newValue: SingleValue<SelectItem>) => {
						setValue('clinic', (newValue as SelectItem) || null, { 
							shouldDirty: true,
							shouldValidate: true 
						});
					}}
					items={clinics}
					isSearchable={true}
					error={errors?.clinic?.message as string | undefined}
				/>
			</GridItem>

			{/* Street Address */}
			<GridItem>
			<FaceliftInput
				topLabel="Street Address"
				placeholder="Type Street Address"
				{...register('ta_streetaddress')}
				error={errors?.ta_streetaddress?.message as string | undefined}
				required
			/>
			</GridItem>

			{/* City */}
			<GridItem>
			<FaceliftInput
				topLabel="City"
				placeholder="City"
				{...register('ta_city')}
				error={errors?.ta_city?.message as string | undefined}
				required
			/>
			</GridItem>

			{/* Zip/Postal Code */}
			<GridItem>
			<FaceliftInput
				topLabel="Zip/Postal Code"
				placeholder="00000"
				inputMode="numeric"
				pattern="[0-9-]*"
				maxLength={10}
				{...stateZipRegister}
				onChange={(e) => {
					// Strip letters/symbols as the user types (allows digits + optional dash)
					e.target.value = e.target.value.replace(/[^\d-]/g, '');
					stateZipRegister.onChange(e);
				}}
				error={errors?.ta_statezip?.message as string | undefined}
				required
			/>
			</GridItem>

			{/* Phone */}
			<GridItem>
			<FaceliftInput
				topLabel="Phone"
				placeholder="000-000-0000"
				{...register('ta_phone')}
				error={errors?.ta_phone?.message as string | undefined}
				required
			/>
			</GridItem>

		{/* Contact (Email) */}
		<GridItem>
		<FaceliftInput
			topLabel="Contact"
			placeholder="xyz@email.com"
			type="email"
			{...register('ta_contact')}
			error={errors?.ta_contact?.message as string | undefined}
			required
		/>
		</GridItem>
			</Grid>
		</Box>
	);
};

export default CustomerInformation;

